import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { toast } from "react-toastify";
import ActionMenu from "../../components/common/ActionMenu";
import CommonPagination from "../../components/common/CommonPagination";
import FormControl from "../../components/common/FormControl";
import {
  MODULE_CONFIG,
  formatDate,
  formatTime,
  capitalize,
  countPermissions,
} from "./shared";
import { hasPermission } from "../../utils/permission";
import {
  FiSearch,
  FiPlus,
  FiEye,
  FiEdit3,
  FiTrash2,
  FiRotateCcw,
  FiX,
  FiMail,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiLock,
} from "react-icons/fi";
import { HiOutlineUserGroup } from "react-icons/hi2";

export default function StaffList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  // Dynamic RBAC Permission Checks for Staff module
  const canCreate = hasPermission(user, "staff", "create");
  const canEdit = hasPermission(user, "staff", "edit");
  const canDelete = hasPermission(user, "staff", "delete");

  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filters & Pagination (Default 25 per page)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Search input handler
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    if (!val || !val.trim()) {
      loader(true);
      setDebouncedSearch("");
      setPage(1);
    }
  };

  // Debounce search input (350ms)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setDebouncedSearch("");
      return;
    }
    const handler = setTimeout(() => {
      loader(true);
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch staff list from API
  const fetchStaff = useCallback(
    async (showLoader = true) => {
      if (showLoader) loader(true);
      setLoading(true);

      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", String(page));
        queryParams.append("limit", String(limit || 25));
        queryParams.append("count", String(limit || 25));

        if (statusFilter && statusFilter !== "all") {
          queryParams.append("status", statusFilter);
        }

        if (debouncedSearch && debouncedSearch.trim()) {
          queryParams.append("search", debouncedSearch.trim());
        }

        const endpoint = `${MODULE_CONFIG.endpoints.listing}?${queryParams.toString()}`;
        const res = await ApiClient.get(endpoint);

        if (res?.success) {
          const data = res?.data;
          const allRecords = Array.isArray(data?.staff)
            ? data.staff
            : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.docs)
            ? data.docs
            : Array.isArray(data?.list)
            ? data.list
            : Array.isArray(data)
            ? data
            : [];

          const dynamicTotal =
            Number(
              res?.total ??
              res?.totalDocs ??
              res?.count ??
              data?.total ??
              data?.totalDocs ??
              data?.count ??
              allRecords.length
            ) || 0;

          setTotalCount(dynamicTotal);

          // If backend returned unsliced records, paginate on client
          const paginatedRecords =
            allRecords.length > (limit || 25)
              ? allRecords.slice((page - 1) * (limit || 25), page * (limit || 25))
              : allRecords;

          setItems(paginatedRecords);
        } else {
          setItems([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error("Failed to fetch staff list:", err);
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        loader(false);
      }
    },
    [page, limit, statusFilter, debouncedSearch]
  );

  useEffect(() => {
    fetchStaff(true);
  }, [fetchStaff]);

  // Reset Filters
  const handleResetFilters = () => {
    loader(true);
    setSearchQuery("");
    setDebouncedSearch("");
    setStatusFilter("all");
    setPage(1);
  };

  // Status Badge Helper (Clickable to Toggle Status)
  const getStatusBadge = (item) => {
    const status = (item?.status || "active").toLowerCase();
    const config = MODULE_CONFIG.statusMap[status] || {
      label: capitalize(status),
      bgClass: "bg-slate-100 text-slate-800 border-slate-300 font-bold",
      dotClass: "bg-slate-400",
    };
    const nextStatus = status === "active" ? "deactive" : "active";

    if (!canEdit) {
      return (
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide border whitespace-nowrap shadow-2xs ${config.bgClass}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${config.dotClass}`} />
          <span>{config.label}</span>
        </span>
      );
    }

    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleStatusChange(item, nextStatus);
        }}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide border whitespace-nowrap cursor-pointer hover:shadow-xs hover:scale-105 active:scale-95 transition-all ${config.bgClass}`}
        title={`Click to mark as ${nextStatus.toUpperCase()}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${config.dotClass}`} />
        <span>{config.label}</span>
      </button>
    );
  };

  // Toggle Staff Status (active <-> deactive)
  const handleStatusChange = async (staff, nextStatus) => {
    const id = staff?._id || staff?.id;
    const staffName =
      `${staff?.firstName || ""} ${staff?.lastName || ""}`.trim() ||
      staff?.email ||
      "Sub Administrator";

    const isDeactivating = nextStatus === "deactive" || nextStatus === "inactive";
    const confirmed = await swal({
      title: `${isDeactivating ? "Deactivate" : "Activate"} Sub Administrator Account?`,
      text: `Are you sure you want to mark ${staffName} as ${nextStatus.toUpperCase()}? ${
        isDeactivating
          ? "This sub administrator will immediately lose access to the administration portal."
          : "The sub administrator account will be re-enabled with its assigned permissions."
      }`,
      icon: isDeactivating ? "warning" : "info",
      buttons: ["Cancel", `Yes, ${isDeactivating ? "Deactivate" : "Activate"}`],
      dangerMode: isDeactivating,
    });

    if (!confirmed) return;

    loader(true);
    try {
      const res = await ApiClient.put(MODULE_CONFIG.endpoints.statusChange, {
        id,
        status: nextStatus,
      });

      if (res?.success) {
        toast.success(`Sub administrator account marked as ${nextStatus.toUpperCase()}.`);
        fetchStaff(false);
      } else {
        toast.error(res?.message || res?.error?.message || "Failed to update sub administrator status.");
      }
    } catch (err) {
      console.error("Status change error:", err);
      toast.error("Failed to update sub administrator status. Please try again.");
    } finally {
      loader(false);
    }
  };

  // Delete Staff Member
  const handleDelete = async (staff) => {
    const id = staff?._id || staff?.id;
    const staffName =
      `${staff?.firstName || ""} ${staff?.lastName || ""}`.trim() ||
      staff?.email ||
      "Sub Administrator";

    const confirmed = await swal({
      title: "Delete Sub Administrator?",
      text: `Are you sure you want to delete ${staffName}? This action permanently revokes all administrative privileges.`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Delete Sub Administrator"],
      dangerMode: true,
    });

    if (!confirmed) return;

    loader(true);
    try {
      const res = await ApiClient.delete(`${MODULE_CONFIG.endpoints.delete}?id=${id}`);
      if (res?.success) {
        toast.success("Sub administrator deleted successfully.");
        fetchStaff(false);
      } else {
        toast.error(res?.message || res?.error?.message || "Failed to delete sub administrator.");
      }
    } catch (err) {
      console.error("Delete staff error:", err);
      toast.error("Failed to delete sub administrator.");
    } finally {
      loader(false);
    }
  };

  const isFilterActive = Boolean(searchQuery.trim() || statusFilter !== "all");

  return (
    <Layout>
      <div className="w-full max-w-full space-y-5 pb-12 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & FILTER BAR (SPACIOUS 2-TIER DESIGN)       */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-4">
          {/* Top Tier: Title, Subtitle & Primary Action */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
            <div className="space-y-1">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                {MODULE_CONFIG.title}
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 font-medium max-w-3xl leading-relaxed">
                {MODULE_CONFIG.subtitle}
              </p>
            </div>

            {/* Add Sub Administrator Button (Protected by RBAC) */}
            {canCreate && (
              <button
                type="button"
                onClick={() => navigate(`${MODULE_CONFIG.routeBase}/add`)}
                className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 h-10 sm:h-11 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs sm:text-sm font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer whitespace-nowrap active:scale-[0.98] shrink-0 self-start sm:self-auto"
              >
                <FiPlus className="w-4 h-4" />
                <span>Add Sub Administrator</span>
              </button>
            )}
          </div>

          {/* Bottom Tier: Filter & Search Controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
              {/* Search Input */}
              <div className="w-full sm:w-80 md:w-96">
                <div className="relative flex items-center group">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-[#115B9D] transition-colors">
                    <FiSearch className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search sub administrators by name, email..."
                    className="w-full bg-white border-[1.5px] border-slate-500 rounded-xl pl-10 pr-9 h-10 text-xs sm:text-sm font-semibold text-slate-900 placeholder:text-slate-500 outline-none focus:outline-none focus:ring-0 focus:border-slate-500 shadow-box"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => handleSearchChange("")}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                      title="Clear search"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Status Filter Dropdown */}
              <div className="w-full sm:w-48 shrink-0">
                <FormControl
                  type="select"
                  placeholder="Filter by Status"
                  value={statusFilter}
                  theme="search"
                  options={MODULE_CONFIG.statusOptions}
                  onChange={(val) => {
                    loader(true);
                    setStatusFilter(val || "all");
                    setPage(1);
                  }}
                />
              </div>

              {/* Reset Filters Button */}
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={!isFilterActive}
                className={`inline-flex items-center justify-center gap-1.5 px-3.5 h-10 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  isFilterActive
                    ? "bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-800 hover:text-slate-950 border border-slate-300 hover:border-slate-400 shadow-xs"
                    : "bg-slate-50 text-slate-400 border border-slate-200 opacity-50 cursor-not-allowed"
                }`}
                title="Reset all search and status filters"
              >
                <FiRotateCcw className={`w-3.5 h-3.5 ${isFilterActive ? "text-slate-700" : "text-slate-400"}`} />
                <span>Reset</span>
              </button>
            </div>

            {/* Total Records Counter */}
            {typeof totalCount === "number" && (
              <div className="text-xs font-semibold text-slate-500 flex items-center gap-2 self-center">
                <span>Total:</span>
                <span className="font-bold text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
                  {totalCount} {totalCount === 1 ? "Administrator" : "Administrators"}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. SUB ADMINISTRATOR LISTING (STICKY HEADER DESKTOP TABLE) */}
        {/* ======================================================== */}
        <div className="bg-transparent md:bg-white md:rounded-2xl md:border md:border-slate-300 md:shadow-[0_2px_10px_rgba(0,0,0,0.03)] overflow-visible">
          {loading ? (
            <div className="py-20 text-center space-y-3">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-300 border-t-[#115B9D]" />
              <p className="text-xs font-bold text-slate-700">Loading sub administrators...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center space-y-3 px-4">
              <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-500 mx-auto flex items-center justify-center">
                <HiOutlineUserGroup className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-900">No Sub Administrators Found</h3>
              <p className="text-xs font-medium text-slate-600 max-w-sm mx-auto">
                {isFilterActive
                  ? "No sub administrators matched your search criteria. Try resetting filters."
                  : "No sub administrator accounts have been created yet. Provision one below."}
              </p>
              <div className="pt-2">
                {isFilterActive ? (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-all border border-slate-300 cursor-pointer"
                  >
                    <FiRotateCcw className="w-3.5 h-3.5" />
                    <span>Clear Search & Filter</span>
                  </button>
                ) : (
                  canCreate && (
                    <button
                      type="button"
                      onClick={() => navigate(`${MODULE_CONFIG.routeBase}/add`)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer"
                    >
                      <FiPlus className="w-3.5 h-3.5" />
                      <span>Add Sub Administrator</span>
                    </button>
                  )
                )}
              </div>
            </div>
          ) : (
            <>
              {/* DESKTOP TABLE VIEW WITH FIXED STICKY HEADER */}
              <div className="hidden md:block w-full">
                <table className="w-full table-fixed border-collapse text-left text-xs">
                  <colgroup>
                    <col style={{ width: "6%" }} />   {/* 1. Action */}
                    <col style={{ width: "32%" }} />  {/* 2. Sub Administrator (Name & Email) */}
                    <col style={{ width: "16%" }} />  {/* 3. Assigned Role */}
                    <col style={{ width: "24%" }} />  {/* 4. Permissions Matrix */}
                    <col style={{ width: "10%" }} />  {/* 5. Status */}
                    <col style={{ width: "12%" }} />  {/* 6. Added Date */}
                  </colgroup>

                  <thead className="sticky top-[72px] z-30 bg-slate-100 shadow-xs">
                    <tr className="border-b border-slate-300 text-[11px] font-black uppercase tracking-wider text-slate-800 bg-slate-100">
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-3 text-center whitespace-nowrap border-b border-slate-300 shadow-xs rounded-tl-2xl">
                        Action
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Sub Administrator
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Role / Privileges
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Granular Permissions
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-center border-b border-slate-300 shadow-xs">
                        Status
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left whitespace-nowrap border-b border-slate-300 shadow-xs rounded-tr-2xl">
                        Added Date
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200">
                    {items.map((item, index) => {
                      const id = item?.id || item?._id;
                      const firstName = item?.firstName || "";
                      const lastName = item?.lastName || "";
                      const fullName =
                        `${firstName} ${lastName}`.trim() || item?.name || "Sub Administrator";
                      const email = item?.email || "";
                      const status = (item?.status || "active").toLowerCase();
                      const createdAt = item?.createdAt || item?.created_at;

                      const { granted, total } = countPermissions(item?.permissions);
                      const isFullAccess = granted === total && total > 0;

                      return (
                        <tr
                          key={id || `staff-${index}`}
                          className="hover:bg-slate-50/80 transition-colors"
                        >
                          {/* 1. Action Dropdown Menu */}
                          <td className="py-3.5 px-3 text-center">
                            <ActionMenu
                              actions={[
                                {
                                  label: "View Dossier",
                                  icon: <FiEye className="w-4 h-4 text-slate-800" />,
                                  onClick: () =>
                                    navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`),
                                },
                                canEdit
                                  ? {
                                      label: "Edit Sub Administrator",
                                      icon: <FiEdit3 className="w-4 h-4 text-slate-800" />,
                                      onClick: () =>
                                        navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`),
                                    }
                                  : null,
                                canDelete
                                  ? {
                                      label: "Delete Sub Administrator",
                                      icon: <FiTrash2 className="w-4 h-4 text-rose-700" />,
                                      onClick: () => handleDelete(item),
                                      isDanger: true,
                                    }
                                  : null,
                              ].filter(Boolean)}
                            />
                          </td>

                          {/* 2. Staff Member Monogram, Full Name & Email */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-8 h-8 rounded-xl bg-slate-100 text-[#115B9D] border border-slate-300 font-black text-xs flex items-center justify-center shrink-0 uppercase shadow-2xs">
                                {fullName.slice(0, 2)}
                              </div>
                              <div className="min-w-0">
                                <div
                                  onClick={() =>
                                    navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)
                                  }
                                  className="font-black text-xs text-slate-950 hover:text-[#115B9D] cursor-pointer truncate transition-colors"
                                >
                                  {fullName}
                                </div>
                                {email && (
                                  <a
                                    href={`mailto:${email}`}
                                    className="text-[11px] font-medium text-slate-700 hover:text-[#115B9D] hover:underline truncate block"
                                  >
                                    {email}
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* 3. Assigned Role / Privileges */}
                          <td className="py-3.5 px-4">
                            <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-[11px] font-bold bg-indigo-50 text-indigo-900 border border-indigo-200">
                              <FiShield className="w-3 h-3 text-indigo-700" />
                              <span>Sub Administrator</span>
                            </div>
                          </td>

                          {/* 4. Granular Permissions Summary Indicator */}
                          <td className="py-3.5 px-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border ${
                                    isFullAccess
                                      ? "bg-purple-50 text-purple-900 border-purple-200"
                                      : granted > 0
                                      ? "bg-blue-50 text-blue-900 border-blue-200"
                                      : "bg-slate-100 text-slate-700 border-slate-300"
                                  }`}
                                >
                                  {isFullAccess
                                    ? "Full Access"
                                    : `${granted} / ${total} Granted`}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* 5. Status Badge (Clickable) */}
                          <td className="py-3.5 px-4 text-center">
                            {getStatusBadge(item)}
                          </td>

                          {/* 6. Added Date */}
                          <td className="py-3.5 px-4 text-left whitespace-nowrap">
                            <div className="text-xs font-bold text-slate-900">
                              {createdAt ? formatDate(createdAt) : "—"}
                            </div>
                            {createdAt && (
                              <div className="text-[11px] font-medium text-slate-600">
                                {formatTime(createdAt)}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARDS VIEW */}
              <div className="block md:hidden space-y-3 p-3">
                {items.map((item, index) => {
                  const id = item?.id || item?._id;
                  const firstName = item?.firstName || "";
                  const lastName = item?.lastName || "";
                  const fullName =
                    `${firstName} ${lastName}`.trim() || item?.name || "Sub Administrator";
                  const email = item?.email || "";
                  const status = (item?.status || "active").toLowerCase();
                  const createdAt = item?.createdAt || item?.created_at;

                  const { granted, total } = countPermissions(item?.permissions);

                  return (
                    <div
                      key={id || `staff-card-${index}`}
                      className="bg-white rounded-2xl p-4 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-3"
                    >
                      {/* Top Row */}
                      <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-xl bg-slate-100 text-[#115B9D] border border-slate-300 font-black text-xs flex items-center justify-center shrink-0 uppercase">
                            {fullName.slice(0, 2)}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-xs sm:text-sm text-slate-900 truncate">
                              {fullName}
                            </h3>
                            {email && (
                              <p className="text-[11px] font-medium text-slate-600 truncate">
                                {email}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {getStatusBadge(item)}
                          <ActionMenu
                            align="right"
                            actions={[
                              {
                                label: "View Dossier",
                                icon: <FiEye className="w-4 h-4 text-slate-800" />,
                                onClick: () =>
                                  navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`),
                              },
                              canEdit
                                ? {
                                    label: "Edit Sub Administrator",
                                    icon: <FiEdit3 className="w-4 h-4 text-slate-800" />,
                                    onClick: () =>
                                      navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`),
                                  }
                                : null,
                              canDelete
                                ? {
                                    label: "Delete Sub Administrator",
                                    icon: <FiTrash2 className="w-4 h-4 text-rose-700" />,
                                    onClick: () => handleDelete(item),
                                    isDanger: true,
                                  }
                                : null,
                            ].filter(Boolean)}
                          />
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex items-center justify-between text-xs pt-1">
                        <span className="font-medium text-slate-600">Permissions:</span>
                        <span className="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200 text-[10px]">
                          {granted} / {total} Actions Granted
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs border-t border-slate-100 pt-2">
                        <span className="font-medium text-slate-500 text-[11px]">Added:</span>
                        <span className="font-bold text-slate-800 text-[11px]">
                          {createdAt ? formatDate(createdAt) : "—"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PAGINATION CONTROLS (Only visible when total records exceed current limit) */}
              {totalCount > limit && (
                <div className="p-4 bg-white border-t border-slate-200 rounded-b-2xl">
                  <CommonPagination
                    page={page}
                    limit={limit}
                    total={totalCount}
                    onPageChange={(p) => {
                      loader(true);
                      setPage(p);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onLimitChange={(l) => {
                      loader(true);
                      setLimit(l);
                      setPage(1);
                    }}
                    limitOptions={[25, 50, 100]}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
